import { createContext, useContext, useMemo, type ReactNode } from "react";
import { User } from "../api/accessControl/User.ts";
import { useSubledgers } from "../hooks/useSubledgers.ts";
import type { Subledger } from "../components/specific/SubledgerSelector";

export interface SubledgerContextValue {
    subledgers: Subledger[];
    isLoading: boolean;
    canUseSubledgers: boolean;
    createSubledger: (name: string) => Promise<Subledger>;
}

export function resolveSubledger(
    subledgerId: string | null | undefined,
    subledgers: Subledger[] | undefined,
): Subledger | null {
    if (!subledgerId || !subledgers) return null;
    return subledgers.find((subledger) => subledger.id === subledgerId) ?? null;
}

async function createSubledgerDisabled(): Promise<Subledger> {
    throw new Error("Subledgers are not enabled for this client");
}

const DISABLED_CONTEXT_VALUE: SubledgerContextValue = {
    subledgers: [],
    isLoading: false,
    canUseSubledgers: false,
    createSubledger: createSubledgerDisabled,
};

const SubledgerContext = createContext<SubledgerContextValue>(DISABLED_CONTEXT_VALUE);

export function SubledgerProvider({
    user,
    clientBusinessId,
    children,
}: {
    user: User;
    clientBusinessId: string | null;
    children: ReactNode;
}) {
    const canUseSubledgers = user.flags.getFlag("PARTNER__CAN_USE_SUBLEDGERS");
    const { subledgers, isLoading, create } = useSubledgers(user, canUseSubledgers ? clientBusinessId : null);

    const value = useMemo<SubledgerContextValue>(() => {
        if (!canUseSubledgers) return DISABLED_CONTEXT_VALUE;
        return { subledgers, isLoading, canUseSubledgers: true, createSubledger: create };
    }, [canUseSubledgers, subledgers, isLoading, create]);

    return <SubledgerContext.Provider value={value}>{children}</SubledgerContext.Provider>;
}

export function useSubledgerContext(): SubledgerContextValue {
    return useContext(SubledgerContext);
}
